/**
 * <load-more-grid>: reveals hidden items in batches when its "Load More" button is clicked.
 * All items are server-rendered up front; this only toggles the `hidden` attribute client-side.
 *
 * Markup contract:
 *   <load-more-grid data-initial-count="12" data-batch-size="12">
 *     <div data-load-more-item>...</div>
 *     ...
 *     <button data-load-more-button>Load More</button>
 *   </load-more-grid>
 */
class LoadMoreGrid extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-load-more-item]'));
    this.originalOrder = this.items.slice();
    this.button = this.querySelector('[data-load-more-button]');
    this.batchSize = parseInt(this.dataset.batchSize, 10) || 12;
    this.initialCount = parseInt(this.dataset.initialCount, 10) || this.batchSize;
    this.visibleCount = this.initialCount;

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.button?.addEventListener('click', this.onButtonClick);
    document.addEventListener('stories-sort:change', this.onSortChange);

    this.update();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onButtonClick);
    document.removeEventListener('stories-sort:change', this.onSortChange);
  }

  onButtonClick() {
    this.visibleCount += this.batchSize;
    this.update();
  }

  onSortChange(event) {
    this.items = event.detail?.sort === 'oldest' ? this.originalOrder.slice().reverse() : this.originalOrder.slice();
    this.items.forEach((item) => this.appendChild(item));
    this.visibleCount = this.initialCount;
    this.update();
  }

  update() {
    this.items.forEach((item, index) => {
      item.hidden = index >= this.visibleCount;
    });

    if (this.button) {
      this.button.hidden = this.visibleCount >= this.items.length;
    }
  }
}

if (!customElements.get('load-more-grid')) {
  customElements.define('load-more-grid', LoadMoreGrid);
}
